import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { LeftSidebarService } from '../../../Common/Layout/services/left-sidebar.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { AmplifyRoutingService } from './amplify-routing.service';
import { WireService } from '../../Wire/services/wire.service';
import { AmplifyRouteModel } from '../../AmplifyRouter/models/amplify-route.model';
import { WireListItemModel } from '../../Wire/models/wire-list-item.model';
import { AmplifyRouteTypeEnum } from '../../AmplifyRouter/enums/amplify-route-type.enum';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { AmplifyDialogComponentTextModel } from '../../../Common/Dialogs/models/amplify-dialog-component-text.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { WireCreateDialogComponent } from '../../Wire/components/WireCreateDialog/wire-create-dialog.component';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DisplayGroupTypeEnum } from '../../../api/displayGroups/enums/display-group-type.enum';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { StoriesConstants } from '../../../News/Newsroom/components/stories/stories-constants';

type GenericLeftSidebarItem = AuditableModel & LeftSidebarListItemConvertableModel;

interface AmplifyLeftSidebarModeConfig {
    mode: Mode;
    isComplete: boolean;
    listItemSource: LeftSidebarListItemConvertableModel[];
    localizations: LeftSidebarLocalizations;
    getButtons: (item: any) => LeftSidebarListItemButton[];
    isAmplifyButtonEnabled: boolean;
    amplifyAction?: () => void;

    // amplifyDialogConfig: AmplifyDialogModel

    filters?: FilterTypeModel[];
    prepareFilters?: () => void; // set name, get checkbox options, etc.
    applyFilters?: (items: LeftSidebarListItemConvertableModel[]) => LeftSidebarListItemConvertableModel[];
}

@Injectable()
export class AmplifyLeftSidebarService extends LeftSidebarService implements OnDestroy {
    private dropdownItems: DropdownOptionModel[];
    private dropdownItemsProvider: BehaviorSubject<DropdownOptionModel[]>;
    private selectedDropdownItem: DropdownOptionModel;
    private selectedDropdownItemProvider: BehaviorSubject<DropdownOptionModel>;

    private listIsComplete: boolean;
    private listIsCompleteProvider: BehaviorSubject<boolean>;
    private selectedListItem: LeftSidebarListItem;
    private selectedListItemProvider: BehaviorSubject<LeftSidebarListItem>;
    private reorderEnabled: boolean;
    private reorderEnabledProvider: BehaviorSubject<boolean>;
    private buttonEnabled: boolean;
    private buttonEnabledProvider: BehaviorSubject<boolean>;

    private localizations: LeftSidebarLocalizations;
    private localizationsProvider: BehaviorSubject<LeftSidebarLocalizations>;
    private amplifyDialogKey: string = null;

    private wires: WireListItemModel[] = [];
    private editorialWires: WireListItemModel[] = [];

    private wireAmplifyDialogLocalizations: AmplifyDialogComponentTextModel = new AmplifyDialogComponentTextModel();
    private editorialAmplifyDialogLocalizations: AmplifyDialogComponentTextModel = new AmplifyDialogComponentTextModel();

    private subscriptions: Subscription[] = [];

    private hiddenListItemsProvider: BehaviorSubject<LeftSidebarListItem[]>;
    private hiddenlistItems: LeftSidebarListItem[];
    private currentModeConfig: AmplifyLeftSidebarModeConfig;

    private itemActionDelete: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-trash-can',
        key: ActionsEnum.delete,
        tooltip: 'Discover.leftSidebar.listItemAction.delete',
        label: 'Lhp.actions.delete',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.delete, icon: ''})
    };

    private itemActionRename: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.rename,
        tooltip: 'Discover.leftSidebar.listItemAction.rename',
        label: 'Lhp.actions.rename',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.rename, icon: ''})
    };

    private itemActionShow: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye',
        key: ActionsEnum.show,
        label: 'Lhp.actions.show',
        tooltip: 'Discover.leftSidebar.listItemAction.show',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.show, icon: ''})
    };

    private itemActionHide: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye-slash',
        key: ActionsEnum.hide,
        label: 'Lhp.actions.hide',
        tooltip: 'Discover.leftSidebar.listItemAction.hide',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.hide, icon: ''})
    };

    private itemActionCopy: LeftSidebarListItemButton = {
        icon: 'fa-kit-duotone fa-agpr-copy',
        key: ActionsEnum.copy,
        tooltip: 'Discover.leftSidebar.listItemAction.edit',
        label: 'Lhp.actions.copy',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.copy, icon: ''})
    };

    private itemActionMove: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.move,
        tooltip: 'Discover.leftSidebar.listItemAction.move',
        label: 'Lhp.actions.move',
        command: (event: any) => this.moveListItem(event.data)
    };

    private itemActionFavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.favorite,
        tooltip: 'Discover.leftSidebar.listItemAction.favorite',
        label: 'Lhp.actions.favorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.favorite, icon: ''})
    };

    private itemActionUnfavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.unfavorite,
        tooltip: 'Discover.leftSidebar.listItemAction.unfavorite',
        label: 'Lhp.actions.unfavorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.unfavorite, icon: ''})
    };

    protected readonly wireStatusFilter = new FilterTypeCheckboxModel<ReleaseStatus>(
        'status',
        'Amplify.leftSidebar.wire.filter.status',
        [
            {
                label: 'Amplify.filter.status.draft',
                value: ReleaseStatus.draft
            },
            {
                label: 'Amplify.filter.status.submitted',
                value: ReleaseStatus.submitted
            },
            {
                label: 'Amplify.filter.status.scheduled',
                value: ReleaseStatus.scheduled
            },
            {
                label: 'Amplify.filter.status.published',
                value: ReleaseStatus.published
            },
            {
                label: 'Amplify.filter.status.retracted',
                value: ReleaseStatus.retracted
            },
            {
                label: 'Amplify.filter.status.failed',
                value: ReleaseStatus.failed
            },
        ]
    );

    private currentRoute: AmplifyRouteModel;

    private amplifyLeftSidebarModeConfigMap: Map<Mode, AmplifyLeftSidebarModeConfig> = new Map([
        [
            Mode.wire,
            {
                mode: Mode.wire,
                isComplete: true,
                listItemSource: this.wires,
                isAmplifyButtonEnabled: true,
                getButtons: this.getWireButtons.bind(this),
                localizations: {
                    amplifyButton: 'Amplify.leftSidebar.amplifyButton.label',
                    itemsHeading: 'Amplify.leftSidebar.wireNameDescription',
                    statusHeading: 'Amplify.leftSidebar.wireStatusDescription',
                    noItems: 'Amplify.leftSidebar.noItems'
                },
                amplifyAction: () => {
                    this.wireService.canDeactivate$().subscribe(
                        result => {
                            if (result) {
                                this.coreDialogService.open(WireCreateDialogComponent);
                            }
                        }
                    );
                },
                filters: [
                    this.nameFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.wireStatusFilter,
                    this.visibilityFilter
                ],
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Amplify.leftSidebar.wire.quickFilter');
                    this.nameFilter.label = 'Amplify.leftSidebar.wire.filter.name';
                    this.getWireStatusTypeFilterCounts(this.wires);
                    this.getAuditableFilterOptions(this.wires);
                    this.getVisibilityFilterOptions(this.visibilityFilter);
                },
                applyFilters: (items: WireListItemModel[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyStatusFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                }
                /* amplifyDialogConfig: {
                    inputs: {
                        datasource: this.listItems,
                        width: 500,
                        display: true,
                        isLoading: false,
                        dropdownDataSource: [],
                        // @TODO: Get the real icon
                        iconClass: 'fa-regular fa-pen',
                        filterPlaceholderText: 'Amplify.wire.amplifyDialog.filterPlaceholderText',
                        localizations: this.wireAmplifyDialogLocalizations
                    },
                    outputs: {
                        dialogClose: this.amplifyClosed,
                        onUseSelected: this.amplifyUseSelected,
                        onCancel: () => this.amplifyCanceled,
                        onCreateNewStory: () => this.amplifyCreateNewStory,
                        onSearch: () => this.amplifyOnSearch
                    }
                }*/
            }
        ],
        [
            Mode.editorial,
            {
                mode: Mode.editorial,
                isComplete: true,
                listItemSource: this.editorialWires,
                isAmplifyButtonEnabled: false,
                getButtons: () => [],
                localizations: {
                    amplifyButton: 'Amplify.leftSidebar.amplifyButton.label',
                    itemsHeading: 'Amplify.leftSidebar.wireNameDescription',
                    statusHeading: 'Amplify.leftSidebar.wireStatusDescription',
                    noItems: 'Amplify.leftSidebar.noItems'
                },
                amplifyDialogConfig: {
                    inputs: {
                        datasource: this.listItems,
                        width: 500,
                        display: true,
                        isLoading: false,
                        dropdownDataSource: [],
                        // @TODO: Get the real icon
                        iconClass: 'fa-regular fa-duotone fa-pen',
                        filterPlaceholderText: 'Amplify.wire.amplifyDialog.filterPlaceholderText',
                        localizations: this.editorialAmplifyDialogLocalizations
                    },
                    outputs: {
                        dialogClose: this.amplifyClosed,
                        onUseSelected: this.amplifyUseSelected,
                        onCancel: () => this.amplifyCanceled,
                        onCreateNewStory: () => this.amplifyCreateNewStory,
                        onSearch: () => this.amplifyOnSearch
                    }
                },
                filters: [
                    this.nameFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.wireStatusFilter,
                    this.visibilityFilter
                ],
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Amplify.leftSidebar.wire.quickFilter');
                    this.nameFilter.label = 'Amplify.leftSidebar.wire.filter.name';

                    this.getWireStatusTypeFilterCounts(this.editorialWires);
                    this.getAuditableFilterOptions(this.editorialWires);
                    this.getVisibilityFilterOptions(this.visibilityFilter);
                },
                applyFilters: (items: WireListItemModel[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyStatusFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                }
            }
        ]
    ]);

    constructor(
        private wireService: WireService,
        private router: Router,
        private discoverRoutingService: DiscoverRoutingService,
        private amplifyRoutingService: AmplifyRoutingService,
        private coreDialogService: CoreDialogService,
        public usersService: UsersService,
        private currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService
    ) {
        super(usersService, displayGroupsService, localizationService);
        this.dropdownItems = [
            new DropdownOptionModel('Core.navigation.mediaRelations.newswire', Mode.wire),
        ];
        this.dropdownItemsProvider = new BehaviorSubject(this.dropdownItems);

        this.selectedDropdownItem = this.dropdownItems[0];
        this.selectedDropdownItemProvider = new BehaviorSubject(this.selectedDropdownItem);
        this.currentMode = this.selectedDropdownItem.value;

        const config = this.amplifyLeftSidebarModeConfigMap.get(this.currentMode);
        this.currentModeConfig = config;
        this.listItems = [];
        this.listItemsProvider = new BehaviorSubject(this.listItems);
        this.listItemGroupsProvider = new BehaviorSubject(this.listItemGroups);
        this.listIsComplete = config.isComplete;
        this.listIsCompleteProvider = new BehaviorSubject(this.listIsComplete);
        this.selectedListItem = null;
        this.selectedListItemProvider = new BehaviorSubject(this.selectedListItem);
        this.reorderEnabled = false;
        this.reorderEnabledProvider = new BehaviorSubject(this.reorderEnabled);
        this.buttonEnabled = config.isAmplifyButtonEnabled;
        this.buttonEnabledProvider = new BehaviorSubject(this.buttonEnabled);
        this.localizations = this.amplifyLeftSidebarModeConfigMap.get(this.currentMode).localizations;
        this.localizationsProvider = new BehaviorSubject(this.localizations);

        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);
        this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldStatus];
        this.lhpSortFields$.next(this.lhpSortFields.slice());
        this.hiddenlistItems = null;
        this.hiddenListItemsProvider = new BehaviorSubject(this.hiddenlistItems);

        this.primaryHeading = 'Amplify.hype.wire.headline';
        this.primaryHeading$.next(this.primaryHeading);

        this.modeProvider$ = new BehaviorSubject(this.currentMode);
        this.createSubscriptions();

        this.topListItem = null;
        this.topListItemProvider = new BehaviorSubject(this.topListItem);
    }


    /**
     * Unsubscribes from the subscriptions when the service is destroyed
     */
    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Updates the sidebar based on the route the user has navigated to
     */
    updateFromRoute(route: AmplifyRouteModel) {
        this.currentRoute = route;
        this.selectedListItem = null;

        switch (route.type) {
            case AmplifyRouteTypeEnum.editorial:
            case AmplifyRouteTypeEnum.editorialHype:
                this.setMode(Mode.editorial);
                break;
            case AmplifyRouteTypeEnum.wire:
                this.setMode(Mode.wire);
                break;
            default:
                this.setMode(Mode.wire);
                break;
        }
        this.updateSelectedDropdownItems();

        this.setSelectedListItem(route);
        this.selectedListItemProvider.next(this.selectedListItem);
    }

    /**
     * Creates the subscriptions
     */
    private createSubscriptions(): void {
        this.subscriptions.push(
            combineLatest([
                this.wireService.all$,
                this.wireService.editorialWireList$,
                this.displayGroupsService.setTypeAndGetGroups(this.modeToGroupTypeMap.get(this.currentMode))
            ]).subscribe(
                ([wires, editorialWires, groups]) => {
                    this.wires.length = 0;
                    this.editorialWires.length = 0;
                    if (wires) {
                        this.wires.push(...wires);
                    }

                    if (editorialWires) {
                        this.editorialWires.push(...editorialWires);
                    }

                    this.listItemGroups = groups.slice();

                    if (this.currentMode === Mode.editorial || this.currentMode === Mode.wire) {
                        this.applyConfig();
                    }
                }
            )
        );

        this.subscriptions.push(
            this.amplifyRoutingService.getCurrentRoute().subscribe(
                (route) => {
                    this.updateFromRoute(route);
                }
            )
        );

        this.currentUserStateService.getPermissions().subscribe(
            permissions => {
                if (!permissions) {
                    return;
                }

                const dropdownItems: DropdownOptionModel[] = [
                    new DropdownOptionModel('Core.navigation.mediaRelations.newswire', Mode.wire)
                ];

                if (permissions.includes(userPermissionsEnum.newsroom)) {
                    dropdownItems.unshift(new DropdownOptionModel('Core.navigation.mediaRelations.newsroom', Mode.newsroom));
                }

                if (permissions.includes(userPermissionsEnum.wireEditorial)) {
                    dropdownItems.push(new DropdownOptionModel('Amplify.leftSidebar.wire.review', Mode.editorial));
                }

                if (permissions.includes(userPermissionsEnum.advancedTargeting)) {
                    dropdownItems.unshift(
                        new DropdownOptionModel('Core.navigation.mediaRelations.savedSearches', Mode.searches),
                        new DropdownOptionModel('Core.navigation.mediaRelations.targetedLists', Mode.targetingLists),
                        new DropdownOptionModel('Core.navigation.mediaRelations.distributions', Mode.distributions),
                    );
                    dropdownItems.push(
                        new DropdownOptionModel('Core.navigation.mediaRelations.privateContacts', Mode.privateContacts)
                    );
                }
                this.dropdownItems = dropdownItems;
                this.dropdownItemsProvider.next(this.dropdownItems);
                this.updateSelectedDropdownItems();
            }
        );
    }

    /**
     * Sets selected list item based on the route.  If route value available, sets selected list item value equal to the route value.
     */
    private setSelectedListItem(route: AmplifyRouteModel): void {
        if (route.value) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = route.value;
        }
    }

    /**
     * Updates the current mode to the provided mode and begins updating the sidebar to match the provided mode's requirements
    */
    private setMode(mode: Mode): void {
        if (this.currentMode === mode) {
            return;
        }

        this.currentMode = mode;
        this.resetFilterOptions();
        this.applyConfig();

        this.modeProvider$.next(this.currentMode);
    }

    private updateSelectedDropdownItems(): void {
        for (const item of this.dropdownItems) {
            if (item.value === this.currentMode) {
                this.selectedDropdownItem = item;
                this.selectedDropdownItemProvider.next(this.selectedDropdownItem);
            }
        }
    }

    applyConfig() {
        const config = this.amplifyLeftSidebarModeConfigMap.get(this.currentMode);

        if (config) {
            const listItems: LeftSidebarListItem[] = config.listItemSource.map(item => item.toLeftSidebarListItem(config.getButtons(item)));

            this.listItems = listItems;
            this.listItemsProvider.next(listItems);
            this.listIsComplete = config.isComplete;
            this.listIsCompleteProvider.next(this.listIsComplete);
            this.buttonEnabled = config.isAmplifyButtonEnabled;
            this.buttonEnabledProvider.next(this.buttonEnabled);
            this.setLocalizations(config.localizations);

            this.setFilters(config.filters);
            config.prepareFilters();
            this.applyFilters();
        }
    }

    /**
     * Gets a list of items to include in the sidebar dropdown
    */
    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItemsProvider;
    }

    /**
     * Gets the currently selected dropdown item
    */
    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItemProvider;
    }

    /**
     * Selects an item in the dropdown
     * @param item An item that exists in the dropdown
    */
    selectDropdownItem(item: DropdownOptionModel): void {
        if (this.dropdownItems.indexOf(item) < 0) {
            throw new Error('Item is not in the list');
        }
        this.selectedDropdownItem = item;
        this.selectedDropdownItemProvider.next(item);

        switch (item.value) {
            case Mode.searches:
                this.discoverRoutingService.gotoTargetingHype();
                break;
            case Mode.targetingLists:
                this.discoverRoutingService.gotoListHype();
                break;
            case Mode.privateContacts:
                this.discoverRoutingService.gotoPrivateContacts();
                break;
            case Mode.distributions:
                this.discoverRoutingService.gotoDistributionHype();
                break;
            case Mode.discoverContacts:
                this.discoverRoutingService.gotoTargetingSearch();
                break;
            case Mode.wire:
                this.amplifyRoutingService.gotoWireHype();
                break;
            case Mode.editorial:
                this.amplifyRoutingService.gotoEditorialHype();
                break;
            case Mode.newsroom:
                this.router.navigate([StoriesConstants.newsroomRootPath]);
                break;
            default:
                break;
        }
    }

    /**
     * Gets currently available items in the list
    */
    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getListIsComplete(): Observable<boolean> {
        return this.listIsCompleteProvider;
    }

    getMoreListItems(): void {
        // not needed for amplify yet
    }

    getSelectedListItem(): Observable<LeftSidebarListItem> {
        return this.selectedListItemProvider;
    }

    /**
     * Responds to a user selecting a list item by taking the action that corresponds with the current mode
     */
    selectListItem(item: LeftSidebarListItem): void {
        switch (this.currentMode) {
            case Mode.wire:
                if (item && item.status === ReleaseStatus.published) {
                    this.amplifyRoutingService.gotoWireReport(item.value);
                } else {
                    this.amplifyRoutingService.gotoWire(item.value);
                }
                break;
            case Mode.editorial:
                this.amplifyRoutingService.gotoEditorial(item.value);
                break;
        }
    }

    /**
     * Responds to the user performing an action from a sidebar list item based on the current mode
     */
    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean = true): void {
        switch (this.currentMode) {
            case Mode.wire:
                this.wireAction(item, button, fromLHP);
                break;
            case Mode.editorial:
                // currently no actions are available in editorial mode
                break;
        }
    }

    /**
     * Opens GroupSelectionDialog for user selection then adds item to the selected group.
     * @param item item to move on confirm
     */
    public moveListItem(item: LeftSidebarListItem) {
        const groupId = item.groupId ? item.groupId : null;
        const showUncategorized = !!item.groupId;
        this.displayGroupsService.openGroupSelectionDialog(groupId, showUncategorized).subscribe(
            (group: DisplayGroupModel) => {
                this.wireService.updateNewswireGroup([item.value], group, item.label);
            }
        );
    }

    /**
     * Handles removing the groupId from all items in a deleted group
     * @param listItems items from the deleted group
     */
    public handleGroupDelete(ids: number[]) {
        this.wireService.removeAllNewswiresFromGroup(ids);
    }

    /**
     * Performs an action on a wire list item based on the provided action and item
     */
    private wireAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const wireListItem = this.wires.find(wire => wire.id === item.value);
        switch (button.key) {
            case ActionsEnum.delete:
                this.wireService.deleteWire(item);
                break;
            case ActionsEnum.copy:
                this.wireService.copyWire(wireListItem);
                break;
            case ActionsEnum.hide:
            case ActionsEnum.show:
                this.wireService.updateVisibility(item.value, button.key);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    this.wireService.openRenameWireDialog(wireListItem);
                } else {
                    this.wireService.renameWire(item.value, item.label);
                }
                break;
            case ActionsEnum.favorite:
                this.wireService.updateNewswireFavorites([item.value], []);
                break;
            case ActionsEnum.unfavorite:
                this.wireService.updateNewswireFavorites([], [item.value]);
                break;
        }
    }

    applyFilters(): void {
        super.applyFilters();
        const config = this.amplifyLeftSidebarModeConfigMap.get(this.currentMode);
        if (config && config.applyFilters) {
            const items = config.applyFilters(config.listItemSource);
            const listItems: LeftSidebarListItem[] = items.map(item => item.toLeftSidebarListItem(config.getButtons(item)));

            const hiddenItems = this.getHiddenItemsFromFilteredItems(this.currentModeConfig.listItemSource);
            const hiddenLHPItems: LeftSidebarListItem[] = hiddenItems.map(item => item.toLeftSidebarListItem(this.currentModeConfig.getButtons(item)));
            this.hiddenListItemsProvider.next(hiddenLHPItems);

            this.listItems = listItems;
            this.groupListItems();
            this.listItemsProvider.next(listItems);
            this.listIsComplete = config.isComplete;
            this.listIsCompleteProvider.next(this.listIsComplete);
        }
    }


    /**
     * Gets a provider for when reordering the left sidebar is enabled
     */
    getReorderEnabled(): Observable<boolean> {
        return this.reorderEnabledProvider;
    }

    /**
     * Sets the left sidebar's order
     */
    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean) {
        // for re-ordering
    }

    /**
     * Checks if the primary button should be enabled.
     */
    public isAmplifyButtonEnabled(): Observable<boolean> {
        return this.buttonEnabledProvider;
    }

    /**
     * Responds to the user clicking the primary call to action
     * in the sidebar
    */
    amplifyButtonClicked(): void {
        this.amplifyLeftSidebarModeConfigMap.get(this.currentMode).amplifyAction();
    }

    /**
     * Gets the current localizations for the left sidebar
     */
    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizationsProvider;
    }
    private getWireButtons(wire: WireListItemModel): LeftSidebarListItemButton[] {
        if (wire.status === ReleaseStatus.published) {
            return [this.itemActionCopy,
                this.itemActionFavorite,
                this.itemActionUnfavorite,
                this.itemActionMove,
                // BUG: https://agility-jira.atlassian.net/browse/NEP-15961 - Can't update visibility of published items
                // TODO add back when bug is resolved.
                // this.itemActionHide,
                // this.itemActionShow
            ];
        }

        return (wire.status === ReleaseStatus.draft || wire.status === ReleaseStatus.submitted) ? [
            this.itemActionRename,
            this.itemActionCopy,
            this.itemActionFavorite,
            this.itemActionUnfavorite,
            this.itemActionMove,
            this.itemActionHide,
            this.itemActionShow,
            this.itemActionDelete
        ] : [
            this.itemActionCopy,
            this.itemActionFavorite,
            this.itemActionUnfavorite,
            this.itemActionMove,
            this.itemActionHide,
            this.itemActionShow,
            this.itemActionDelete
        ];

    }

    /**
     * Sets the current localizations
     */
    private setLocalizations(localizations: LeftSidebarLocalizations): void {
        this.localizations = localizations;
        this.localizationsProvider.next(localizations);
    }

    private amplifyClosed(confirm: boolean) {
        if (confirm) {
            // Make a new one
        }
    }

    private amplifyUseSelected() {
        // @TODO: something goes here?
    }

    private amplifyCanceled() {
        this.coreDialogService.close(this.amplifyDialogKey);
    }

    private amplifyCreateNewStory() {
        // @TODO: something goes here?
    }

    private amplifyOnSearch() {
        // @TODO: something goes here?
    }

    public getWireStatusTypeFilterCounts(items: WireListItemModel[], filter: FilterTypeCheckboxModel = this.wireStatusFilter) {
        const optionsMap: Map<ReleaseStatus, FilterOptionModel<ReleaseStatus>> = new Map();
        filter.options.forEach(opt => {
            opt.count = 0;
            optionsMap.set(opt.value, opt);
        });
        items.forEach(item => {
            if (item.status) {
                optionsMap.get(item.status).count++;
            }
        });
    }

    public applyStatusFilters(items: WireListItemModel[], filter: FilterTypeCheckboxModel = this.wireStatusFilter): WireListItemModel[] {
        const filterData = filter.getValue();

        if (!filterData || filterData.length < 1) {
            return items;
        }

        return items.filter(item => {
            const status = item.toLeftSidebarListItem().status as ReleaseStatus;
            return filterData.includes(status);
        });
    }

    /**
     * Resets all of the filters
     */
    private resetFilterOptions(): void {
        this.nameFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdByFilter.options = [];
        this.modifiedByFilter.setValue([]);
        this.modifiedByFilter.options = [];
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.wireStatusFilter.setValue([]);
    }

    private getHiddenItemsFromFilteredItems(
        items: LeftSidebarListItemConvertableModel[]
    ): LeftSidebarListItemConvertableModel[] {
        const value = this.visibilityFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
            case ListEntitiesVisibilityEnum.hidden:
                // There are no "hidden" items that are not currently showing if we are explicitly showing them
                return [];
            case ListEntitiesVisibilityEnum.visible:
            default:
                // can be null as there are no options/starting value for the visibility filter
                return items.filter(item  => (item as GenericLeftSidebarItem).hidden);
        }
    }

    getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return this.hiddenListItemsProvider;
    }



    private applyVisibilityFilter<T extends AuditableModel>(items: T[]): T[] {
        const value = this.visibilityFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
                return items;
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => !item.hidden);
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => item.hidden);
            default:
                return items.filter(item => !item.hidden);
        }
    }

    setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all) {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

}
